/**
 * Typewriter effect for the markdown source view.
 * Renders the fully styled span tree upfront, then reveals it
 * character by character so styling is present throughout.
 */

const CHARS_PER_SECOND = 250;

// Pre-compute reveal timestamps for each character with natural variation.
// 6% of chars get a micro-pause (3–7× base interval); the rest vary 0.4–1.6×.
function buildTimings(totalChars) {
  const base = 1000 / CHARS_PER_SECOND;
  const timings = new Float32Array(totalChars);
  let t = 0;
  for (let i = 0; i < totalChars; i++) {
    const r = Math.random();
    const mult =
      r < 0.06 ? 3 + Math.random() * 4 : 0.4 + Math.random() * 1.2;
    t += base * mult;
    timings[i] = t;
  }
  return timings;
}

/**
 * Walk all text nodes within a container in document order,
 * returning an array of { node, full } objects.
 * @param {HTMLElement} container
 * @returns {{ node: Text, full: string }[]}
 */
function collectTextNodes(container) {
  const result = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    if (node.nodeValue) result.push({ node, full: node.nodeValue });
  }
  return result;
}

/**
 * Animate the markdown source view with a typewriter effect.
 * Calls renderFn to build the styled DOM first, then reveals it character by character.
 * A blinking cursor tracks the last typed character and is removed when done.
 * @param {string} text - Raw markdown string
 * @param {HTMLElement} container - The <pre> element to animate into
 * @param {function(string, HTMLElement): void} renderFn - renderMarkdownSource
 */
export function typewriteMarkdownSource(text, container, renderFn) {
  // Build the full styled tree
  renderFn(text, container);

  // Collect all text nodes and blank them out
  const nodes = collectTextNodes(container);
  const totalChars = nodes.reduce((sum, n) => sum + n.full.length, 0);
  nodes.forEach(({ node }) => {
    node.nodeValue = '';
  });

  // Pre-compute organic char timings
  const timings = buildTimings(totalChars);

  // Create the blinking cursor element
  const cursor = document.createElement('span');
  cursor.className = 'tw-cursor';
  container.appendChild(cursor);

  let revealed = 0;
  let startTime = null;
  let lastActiveIndex = -1;

  function placeCursor(activeNodeIndex) {
    if (activeNodeIndex === lastActiveIndex) return;
    lastActiveIndex = activeNodeIndex;
    const { node } = nodes[activeNodeIndex];
    // Insert cursor immediately after the text node's parent element
    const parent = node.parentElement;
    if (parent && parent !== container) {
      parent.after(cursor);
    }
  }

  function step(timestamp) {
    if (startTime === null) startTime = timestamp;
    const elapsed = timestamp - startTime;

    let target = revealed;
    while (target < totalChars && timings[target] <= elapsed) target++;

    if (target !== revealed) {
      // Distribute revealed characters across text nodes in order
      let remaining = target;
      let activeIndex = 0;
      for (let i = 0; i < nodes.length; i++) {
        const { node, full } = nodes[i];
        if (remaining <= 0) {
          node.nodeValue = '';
        } else if (remaining >= full.length) {
          node.nodeValue = full;
          remaining -= full.length;
          if (remaining === 0) activeIndex = i;
        } else {
          node.nodeValue = full.slice(0, remaining);
          activeIndex = i;
          remaining = 0;
        }
      }
      placeCursor(activeIndex);
      revealed = target;
    }

    if (revealed < totalChars) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}
